import * as React from "react";
import * as T from "types";
import * as Ui from "semantic-ui-react";
import { useNavigate, useParams } from "react-router-dom";
import { CopyableText } from "./CopyableText";
import {
  groupChannelsByCategory,
  isOldChannel,
  sortChannels,
} from "utils/channelCategories";
import { ArchivedChannel } from "@fotokite/archive-admin-api";

interface Props {
  allObjects: T.StorageMetadataList;
  archivedChannels: ArchivedChannel[];
}

interface UrlParams {
  channel_old: string;
  version_old: string;
  channel_new: string;
  version_new: string;
}

export const ChooseRel: React.FC<Props> = (p) => {
  // Init state
  const params = useParams() as any as UrlParams;
  const navigate = useNavigate();

  const updateUrl = (newParams: UrlParams) => {
    navigate(
      `/release/${newParams.channel_old}/${newParams.version_old}/${newParams.channel_new}/${newParams.version_new}`
    );
  };

  const channelNames = T.groupChannels(p.allObjects)
    .map((group) => group.first(null)?.fkChannel)
    .filter((channel): channel is string => !!channel)
    .toArray();

  const flatArchivedChannels = p.archivedChannels.map(
    (channel) => channel.channel ?? ""
  );

  const archivedDateByChannel = new Map(
    p.archivedChannels.map((channel) => [
      channel.channel ?? "",
      channel.timeOfArchiving,
    ])
  );

  const activeChannelNames = channelNames.filter(
    (channel) => !isOldChannel(flatArchivedChannels, channel)
  );
  const archivedChannelNames = channelNames.filter((channel) =>
    isOldChannel(flatArchivedChannels, channel)
  );

  const buildGroupOptions = (
    label: string,
    channels: string[],
    archived?: boolean
  ) => {
    if (channels.length === 0) return [];

    return [
      {
        key: `header-${label}`,
        value: undefined,
        text: label,
        disabled: true,
        className: "channel-group-header",
      },
      ...Array.from(channels)
        .sort(sortChannels)
        .map((channel) => {
          const archivedDate = archived
            ? archivedDateByChannel.get(channel)
            : undefined;

          return {
            key: channel,
            value: channel,
            text: archivedDate
              ? `${channel} (archived ${archivedDate
                  .toISOString()
                  .slice(0, 10)})`
              : channel,
            className: archived
              ? "channel-option-item channel-option-item--archived"
              : "channel-option-item",
          };
        }),
    ];
  };

  const categorizedChannels = groupChannelsByCategory(activeChannelNames);

  const channelOptions = [
    ...Object.values(categorizedChannels).flatMap((group) =>
      buildGroupOptions(group.label, Array.from(group.channels))
    ),
    ...buildGroupOptions("Archived Channels", archivedChannelNames, true),
  ];

  return (
    <div>
      {/* Semantic UI forces opacity/color/padding on dropdown items via
          !important, which drowns out our group headers and blocks
          indenting channels under them - override both with matching
          !important rules. */}
      <style>{`
        .ui.dropdown .menu > .item.channel-group-header.disabled {
          opacity: 1 !important;
          color: rgba(0, 0, 0, 0.85) !important;
          background: rgba(0, 0, 0, 0.2) !important;
          font-weight: bold !important;
          text-transform: uppercase;
          font-size: 0.78em;
          letter-spacing: 0.04em;
        }
        .ui.dropdown .menu > .item.channel-option-item {
          padding-left: 2.5em !important;
          white-space: nowrap;
        }
        .ui.dropdown .menu > .item.channel-option-item--archived {
          color: rgba(0, 0, 0, 0.4) !important;
        }
        .ui.dropdown.channel-dropdown > .menu {
          max-height: 80vh !important;
          min-width: 100% !important;
        }
      `}</style>
      <Ui.Form>
        <Ui.Form.Group>
          <Ui.Form.Field inline>
            <Ui.Label color="red">
              From
              <CopyableText
                text={`${params.channel_old}.v${params.version_old}`}
                displayText=""
              />
            </Ui.Label>

            <Ui.Dropdown
              placeholder="Select Channel"
              search
              selection
              className="channel-dropdown"
              style={{ minWidth: "320px" }}
              value={params.channel_old}
              options={channelOptions}
              onChange={(_, { value }) => {
                let copy = params;
                copy.channel_old = (value as string) || "-";
                copy.version_old = `${T.findLatestVer(
                  p.allObjects,
                  copy.channel_old
                )}`;
                updateUrl(copy);
              }}
            />
          </Ui.Form.Field>
          <Ui.Form.Field inline>
            <Ui.Input
              value={params.version_old}
              type="number"
              onChange={(_, data) => {
                let copy = params;
                copy.version_old = data.value || "-";
                updateUrl(copy);
              }}
              style={{ maxWidth: "80px" }}
            />
          </Ui.Form.Field>
          <Ui.Button
            icon
            title="Go to latest available version"
            onClick={() => {
              let copy = params;
              copy.version_old = `${T.findLatestVer(
                p.allObjects,
                params.channel_old
              )}`;
              updateUrl(copy);
            }}
          >
            <Ui.Icon name="angle double up" />
          </Ui.Button>
          &nbsp; &nbsp;
          <Ui.Button
            icon
            circular
            color="black"
            title="Swap old/new"
            onClick={() => {
              let copy = params;
              [copy.version_new, copy.version_old] = [
                copy.version_old,
                copy.version_new,
              ];
              [copy.channel_new, copy.channel_old] = [
                copy.channel_old,
                copy.channel_new,
              ];
              updateUrl(copy);
            }}
          >
            <Ui.Icon name="arrow left" />
            <Ui.Icon name="arrow right" />
          </Ui.Button>
          &nbsp;
          <Ui.Form.Field inline>
            <Ui.Label color="green">
              To
              <CopyableText
                text={`${params.channel_new}.v${params.version_new}`}
                displayText=""
              />
            </Ui.Label>
            <Ui.Dropdown
              placeholder="Select Channel"
              search
              selection
              className="channel-dropdown"
              style={{ minWidth: "320px" }}
              value={params.channel_new}
              options={channelOptions}
              onChange={(_, { value }) => {
                let copy = params;
                copy.channel_new = (value as string) || "-";
                copy.version_new = `${T.findLatestVer(
                  p.allObjects,
                  copy.channel_new
                )}`;
                updateUrl(copy);
              }}
            />
          </Ui.Form.Field>
          <Ui.Form.Field inline>
            <Ui.Input
              value={params.version_new}
              type="number"
              onChange={(_, data) => {
                let copy = params;
                copy.version_new = data.value || "-";
                updateUrl(copy);
              }}
              style={{ maxWidth: "80px" }}
            />
          </Ui.Form.Field>
          <Ui.Button
            icon
            title="Go to latest available version"
            onClick={() => {
              let copy = params;
              copy.version_new = `${T.findLatestVer(
                p.allObjects,
                params.channel_new
              )}`;
              updateUrl(copy);
            }}
          >
            <Ui.Icon name="angle double up" />
          </Ui.Button>
        </Ui.Form.Group>
      </Ui.Form>
    </div>
  );
};
